import React from 'react'
import { boolean, number, object, setLocale, string } from 'yup'
import { Col, Dropdown, Row, Tag, Tooltip } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import { FormikProps } from 'formik'
import { SelectValue } from 'antd/lib/select'

import { BillingType, Id, Organization } from 'src/types'
import {
  Breadcrumb,
  Button,
  Menu,
  MenuItem,
  Text,
  ToolIcon
} from 'src/components/Ant'
import {
  Input,
  SelectOrganization,
  Switch as SwitchFormik
} from 'src/components/Formik'
import { T, stringify } from 'src/core/hooks/use-translation'

import { DataColumnsProps } from '../../../components/resources-crud'

export function getDataColumns({
  editCurrent
}: DataColumnsProps): ColumnProps<unknown>[] {
  return [
    {
      dataIndex: 'code',
      render: (code: string) => {
        if (code?.length > 50) {
          return <Tooltip title={code}>{`${code.substring(0, 47)}...`}</Tooltip>
        }
        return <Text>{code}</Text>
      },
      title: 'web.resources.common.code.title'
    },
    {
      dataIndex: 'name',
      elipsis: true,
      render: (name: string = '') => {
        if (name?.length > 50) {
          return <Tooltip title={name}>{`${name.substring(0, 47)}...`}</Tooltip>
        }
        return <Text>{name}</Text>
      },
      title: 'web.resources.common.name.title'
    },
    {
      dataIndex: 'description',
      elipsis: true,
      render: (description: string = '') => {
        if (description?.length > 50) {
          return (
            <Tooltip title={description}>
              {`${description.substring(0, 47)}...`}
            </Tooltip>
          )
        }
        return <Text>{description}</Text>
      },
      title: 'web.resources.common.description.title'
    },
    {
      dataIndex: 'organization',
      render: (organization: Organization) => (
        <Tag color="green">
          <Text>{organization?.trade_name || ''}</Text>
        </Tag>
      ),
      title: 'web.resources.organizations.title'
    },
    {
      dataIndex: 'standard',
      render: (isStandard: boolean) =>
        isStandard && (
          <Tag color="blue">
            <Text title="web.resources.common.standard" />
          </Tag>
        ),
      title: 'web.resources.common.standard'
    },
    {
      dataIndex: 'actions',
      render: (_: any, record: BillingType) => {
        const actionSetActive = record?.active
          ? 'common.actions.inactivate'
          : 'common.actions.enable'
        const actionSetStandard = record?.standard
          ? 'web.administration.crud.actions.standard-remove'
          : 'web.administration.crud.actions.standard-set'
        const actionEdit = 'web.administration.crud.actions.edit'

        return (
          <Dropdown
            overlay={
              record.locked ? (
                <Menu>
                  <MenuItem
                    key="locked"
                    title="web.resources.billing-types.locked-info"
                  />
                </Menu>
              ) : (
                <Menu
                  onClick={async ({ key }: any) => {
                    switch (key) {
                      case 'set-standard':
                        editCurrent(
                          {
                            ...record,
                            standard: !record.standard
                          },
                          true
                        )
                        break
                      case 'edit':
                        editCurrent(record, false)
                        break
                      case 'set-active':
                        editCurrent({ ...record, active: false }, true)
                        break
                    }
                  }}
                >
                  <MenuItem key="set-standard" title={actionSetStandard} />
                  <MenuItem key="edit" title={actionEdit} />
                  <MenuItem key="set-active" title={actionSetActive} />
                </Menu>
              )
            }
            placement="bottomRight"
          >
            <Button type="link">
              <ToolIcon type="more" style={{ fontWeight: 'bold' }} />
            </Button>
          </Dropdown>
        )
      },
      width: 50
    }
  ] as ColumnProps<unknown>[]
}

export function getDrawer(isSuperAdmin: boolean) {
  return function DrawerContent(
    formikProps: FormikProps<BillingType>
  ): JSX.Element {
    if (!formikProps) {
      return <span />
    }

    const TLocked = <T title="web.resources.billing-types.locked" />

    return (
      <>
        <Row gutter={[12, 0]}>
          <Col span={8}>
            <Input
              formikProps={formikProps}
              label="web.resources.common.code.title"
              name="code"
              placeholder="web.resources.common.code.placeholder"
              required={false}
            />
          </Col>
          <Col span={16}>
            <Input
              formikProps={formikProps}
              label="web.resources.common.name.title"
              name="name"
              placeholder="web.resources.common.name.placeholder"
            />
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Input
              formikProps={formikProps}
              label="web.resources.common.description.title"
              name="description"
              placeholder="web.resources.common.description.placeholder"
              required={false}
            />
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <SelectOrganization
              disabled={!isSuperAdmin}
              formikProps={formikProps}
              label="web.resources.organizations.title"
              onChange={(value: SelectValue) => {
                formikProps.setFieldValue('organization_id', value)
              }}
              style={{ width: '100%', minWidth: '150px' }}
            />
          </Col>
        </Row>
        <Row>
          <Col span={12}>
            <SwitchFormik
              checkedChildren={TLocked}
              disabled={!isSuperAdmin}
              formikProps={formikProps}
              name="locked"
              unCheckedChildren={TLocked}
            />
          </Col>
        </Row>
      </>
    )
  }
}

export const getDrawerWidth = '40%'

export const getHeader: JSX.Element = (
  <Breadcrumb
    items={[
      'web.administration.title',
      'web.resources.title-plural',
      'web.resources.billing-types.title-plural'
    ]}
  />
)

export function getInitialValues(organization_id: Id): BillingType {
  return {
    active: true,
    billing_type_id: null,
    code: '',
    description: '',
    locked: false,
    name: '',
    organization_id,
    // shared_with: null,
    standard: false
  } as BillingType
}

export const getTitle = 'web.resources.billing-types.title'

export function getValidationSchema() {
  setLocale({
    mixed: {
      required: 'common.required',
      notType: 'common.required'
    }
  })
  return object().shape({
    active: boolean().required(),
    code: string()
      .max(
        250,
        stringify('web.setup.form.field-title.max-character-error', {
          value: 250
        })
      )
      .notRequired(),
    description: string()
      .max(
        2000,
        stringify('web.setup.form.field-title.max-character-error', {
          value: 2000
        })
      )
      .notRequired(),
    locked: boolean().required(),
    name: string()
      .trim()
      .max(
        250,
        stringify('web.setup.form.field-title.max-character-error', {
          value: 250
        })
      )
      .required(),
    organization_id: number().required().positive('common.required'),
    standard: boolean().required()
  })
}
